// packages/app/src/components/techdocs/DocsHomePage.tsx
//
// Custom /docs landing page, replacing @backstage/plugin-techdocs's
// <DefaultTechDocsHome />.
//
// WHY THIS EXISTS:
//   Backstage's built-in DocsCardGrid/DocsTable components render their own
//   "No documents to show / Getting Started Information" empty state with a
//   hardcoded link to https://backstage.io/docs/features/techdocs/getting-started.
//   As of @backstage/plugin-techdocs ^1.17, that link is NOT exposed as a
//   prop or translation key — only the surrounding text is translatable, the
//   href itself is a string literal in the component source. So it can't be
//   re-pointed at our internal docs without either patching node_modules or
//   building our own home page. We do the latter, using Backstage's own
//   documented "Building a Custom home page" pattern:
//   https://backstage.io/docs/features/techdocs/techdocs-how-to-guides--old/#building-a-custom-home-page
//
// HOW IT AVOIDS THE HARDCODED LINK:
//   We call useEntityList() ourselves (the same hook EntityListDocsGrid uses
//   internally) and check the result count BEFORE rendering
//   EntityListDocsGrid. If there are zero matching docs, we render our own
//   IdpAnnotationEmptyState (same component + internal Confluence link used
//   on the entity-page CI/CD and Pull Requests tabs) instead of ever letting
//   EntityListDocsGrid reach its own built-in empty state.
import { ReactNode } from 'react';
import { Entity } from '@backstage/catalog-model';
import { Content, Progress } from '@backstage/core-components';
import {
  CatalogFilterLayout,
  EntityOwnerPicker,
  EntityTagPicker,
  UserListPicker,
  EntityListProvider,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import {
  TechDocsPageWrapper,
  TechDocsPicker,
  EntityListDocsGrid,
} from '@backstage/plugin-techdocs';

import { IdpAnnotationEmptyState } from '../catalog/EntityPage';

export type DocsHomePageProps = {
  groups?: Array<{
    title: ReactNode;
    filterPredicate: ((entity: Entity) => boolean) | string;
  }>;
};

/**
 * Pure presentational branch: given loading/result state, decides whether to
 * show a loading spinner, our internally-branded empty state, or the
 * provided grid content. Extracted from DocsHomePageContent (which sources
 * loading/hasResults from useEntityList()) specifically so this decision
 * logic is unit-testable without mocking the full EntityListProvider /
 * catalogApi context stack.
 */
export function DocsHomePageState({
  loading,
  hasResults,
  children,
}: {
  loading: boolean;
  hasResults: boolean;
  children: ReactNode;
}) {
  if (loading) {
    return <Progress />;
  }

  if (!hasResults) {
    return (
      <IdpAnnotationEmptyState
        title="Developer Team User Guide"
        description="Internal Developer Platform documentation to get started."
      />
    );
  }

  return <>{children}</>;
}

// Must render inside <EntityListProvider> — useEntityList() reads from that
// context (the same context EntityListDocsGrid and the filter pickers below
// consume).
function DocsHomePageContent({ groups }: DocsHomePageProps) {
  const { entities, loading } = useEntityList();

  return (
    <DocsHomePageState loading={loading} hasResults={entities.length > 0}>
      <EntityListDocsGrid groups={groups} />
    </DocsHomePageState>
  );
}

export const DocsHomePage = ({ groups }: DocsHomePageProps) => {
  return (
    <TechDocsPageWrapper>
      <Content>
        <EntityListProvider>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <TechDocsPicker />
              <UserListPicker initialFilter="all" />
              <EntityOwnerPicker />
              <EntityTagPicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <DocsHomePageContent groups={groups} />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Content>
    </TechDocsPageWrapper>
  );
};
